import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../render/layout';
import { makeTwoPassText } from '../render/twoPassText';
import { makePawButton, type PawButton } from '../render/standardButton';

interface DogTrainingPage {
  // Either a video key OR 'doghouse' to build the close-up composition.
  video: string | 'doghouse';
  text: string;
  videoScaleX: number;
  videoScaleY: number;
  textYOffset?: number;
}

const PAGES: DogTrainingPage[] = [
  {
    video: 'BigPuppy',
    text:
      'This Puppy wants to play on the grass and you can only pick a spot next to the same breed or color. They also like fire hydrants!',
    videoScaleX: 1.15,
    videoScaleY: 1.15,
  },
  {
    video: 'MegaPuppy',
    text:
      'Four Puppies of the same breed in a square create a MegaPuppy and if they are the same color, a SuperMegaPuppy! Tap one to get a CrazySquare! Which you may need later on!',
    videoScaleX: 0.855,
    videoScaleY: 1.035,
    textYOffset: -40,
  },
  {
    video: 'ClearGrass',
    text:
      'Once they have played on an area it gets cleared. Puppies play around obstacles — a park bench, merry-go-round, horsey, and seesaw. Sometimes the grass grows back from a sprinkler or thunderstorm.',
    videoScaleX: 0.85,
    videoScaleY: 1.15,
    textYOffset: -40,
  },
  {
    video: 'doghouse',
    text:
      "If the Puppy doesn't have any place to play, you can use a CrazySquare to find a different Puppy. Or you can put the Puppy in the Dog House... but only 3 Puppies can fit!",
    videoScaleX: 1.15,
    videoScaleY: 1.15,
    textYOffset: -100,
  },
];

interface DogTrainingInit {
  onComplete?: () => void;
}

export class DogTrainingScene extends Phaser.Scene {
  private pageIndex = 0;
  private pageContainer: Phaser.GameObjects.Container | null = null;
  private nextButton!: PawButton;
  private onComplete: (() => void) | null = null;

  constructor() {
    super({ key: 'DogTraining' });
  }

  init(data?: DogTrainingInit): void {
    this.pageIndex = 0;
    this.onComplete = data?.onComplete ?? null;
  }

  create(): void {
    this.add
      .rectangle(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0x1f4711)
      .setOrigin(0, 0)
      .setDepth(1000);

    makeTwoPassText(this, LOGICAL_WIDTH / 2, 80, 'Dog Training', {
      fontSize: '72px',
      depth: 1010,
    });

    // Play! — new paw-button style (red paw, salmon fill, red border).
    makePawButton({
      scene: this,
      x: LOGICAL_WIDTH / 2,
      y: LOGICAL_HEIGHT - 90,
      label: 'Play!',
      fontSize: '40px',
      baseDepth: 1010,
      onClick: () => this.dismiss(),
    });

    // Next — same style + chevrons on the right. Positioned with right edge
    // near the screen edge; the helper centers the content inside the pill.
    this.nextButton = makePawButton({
      scene: this,
      x: LOGICAL_WIDTH - 110,
      y: LOGICAL_HEIGHT - 180,
      label: 'Next',
      fontSize: '40px',
      showChevrons: true,
      baseDepth: 1010,
      onClick: () => this.advance(),
    });

    this.buildPage(this.pageIndex, 0);
  }

  shutdown(): void {
    if (this.pageContainer) {
      this.pageContainer.destroy(true);
      this.pageContainer = null;
    }
  }

  private buildPage(idx: number, offsetX: number): Phaser.GameObjects.Container {
    const page = PAGES[idx]!;
    const container = this.add.container(offsetX, 0).setDepth(1005);

    if (page.video === 'doghouse') {
      this.buildDogHouseScene(container);
    } else {
      const video = this.add.video(LOGICAL_WIDTH / 2, LOGICAL_HEIGHT / 2 - 120, page.video);
      video.setMute(true);
      video.setLoop(true);
      video.setScale(page.videoScaleX, page.videoScaleY);
      video.play();
      container.add(video);
    }

    const text = this.add
      .text(40, LOGICAL_HEIGHT / 2 + 280 + (page.textYOffset ?? 0), page.text, {
        fontFamily: 'Fredoka',
        fontSize: '28px',
        color: '#ffffff',
        wordWrap: { width: LOGICAL_WIDTH - 80 },
        align: 'left',
      })
      .setOrigin(0, 0);
    container.add(text);

    this.pageContainer = container;
    const showNext = idx < PAGES.length - 1;
    for (const obj of this.nextButton.objects) {
      (obj as Phaser.GameObjects.Image).setVisible(showNext);
    }
    return container;
  }

  // Composed close-up that replaces the DogHouse.webm video — sky + ground,
  // the doghouse, a dog bowl beside it, and 3 sets of eyes peeking through
  // the doghouse window. The eyes randomly retexture every 1 second (same
  // animation as the live header).
  private buildDogHouseScene(container: Phaser.GameObjects.Container): void {
    // Match the bounding rect the original 518x366 video occupied at scale
    // 1.15: ~596x421 centered at (LOGICAL_WIDTH/2, LOGICAL_HEIGHT/2 - 120).
    const rectW = 596;
    const rectH = 421;
    const cx = LOGICAL_WIDTH / 2;
    const cy = LOGICAL_HEIGHT / 2 - 120;
    const rectX = cx - rectW / 2;
    const rectY = cy - rectH / 2;

    // Sky fills the whole rect behind everything else. Ground keeps its
    // native aspect ratio (1242x289 = ~4.3:1) so the trees don't squash
    // horizontally, and the bottom edge aligns to the rect's bottom edge.
    // The transparent area above the trees lets the sky show through.
    const sky = this.add
      .image(rectX, rectY, 'sky6plus')
      .setOrigin(0, 0)
      .setDisplaySize(rectW, rectH);
    const groundAspect = 1242 / 289;
    const groundDispH = Math.round(rectW / groundAspect);
    const groundY = rectY + rectH - groundDispH;
    container.add(sky);

    // Drifting clouds, layered between the sky and the ground so the trees
    // can eclipse them where they overlap (matches header behaviour). Each
    // cloud drifts L→R and wraps when it passes the right edge.
    // A geometry mask shaped to the rect crops the clouds at the edges so
    // they don't bleed into the dark-green backdrop around the rectangle.
    const maskShape = this.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(rectX, rectY, rectW, rectH);
    maskShape.setVisible(false);
    const cloudMask = maskShape.createGeometryMask();

    const cloudKeys = ['cloud1', 'cloud2', 'cloud3'];
    const cloudYs = [rectY + 40, rectY + 90, rectY + 140];
    const cloudSpeeds = [22, 14, 18]; // px/sec
    const cloudDelays = [0, 9000, 18000]; // ms
    const leftWrap = rectX - 80;
    const rightWrap = rectX + rectW + 80;
    for (let i = 0; i < cloudKeys.length; i++) {
      const cloud = this.add
        .image(leftWrap, cloudYs[i]!, cloudKeys[i]!)
        .setScale(1.0)
        .setOrigin(0.5)
        .setMask(cloudMask);
      container.add(cloud);
      const drift = ((rightWrap - leftWrap) / cloudSpeeds[i]!) * 1000;
      this.tweens.add({
        targets: cloud,
        x: rightWrap,
        duration: drift,
        delay: cloudDelays[i]!,
        ease: 'Linear',
        repeat: -1,
        onRepeat: () => cloud.setX(leftWrap),
      });
    }
    container.once(Phaser.GameObjects.Events.DESTROY, () => maskShape.destroy());

    const ground = this.add
      .image(rectX, groundY, 'ground6plus')
      .setOrigin(0, 0)
      .setDisplaySize(rectW, groundDispH);
    container.add(ground);

    // Doghouse — 199x137 native; scale up so it dominates the close-up.
    const dhW = 340;
    const dhH = Math.round(dhW * (137 / 199));
    const dhX = cx - 30; // shift slightly left to leave room for the bowl
    // Doghouse + bowl sit ~70% down the rect, then nudged up 10 px.
    const dhY = rectY + Math.round(rectH * 0.7) - 10;
    const doghouse = this.add
      .image(dhX, dhY, 'doghouse2')
      .setDisplaySize(dhW, dhH)
      .setOrigin(0.5);
    container.add(doghouse);

    // Eyes peeking through the window — same offset/nudge model as the live
    // header (HeaderView). Three pairs, tracked in `eyes` so the timer below
    // can re-randomize their textures every second.
    const eyeScale = dhW / 87;
    const eyeW = 22 * eyeScale * 0.5;
    const eyeH = 16 * eyeScale * 0.5;
    const eyeOffsets = [
      { x: -31, y: 3,  nudgeX: 15,  nudgeY: 6.25, frame: 'eyes2@2x.png' },
      { x:  14, y: 4,  nudgeX: 7.5, nudgeY: 0,    frame: 'eyes1@2x.png' },
      { x:  19, y: 16, nudgeX: 0,   nudgeY: 0,    frame: 'eyes3@2x.png' },
    ];
    const eyes = eyeOffsets.map(({ x, y, nudgeX, nudgeY, frame }) =>
      this.add
        .image(dhX + x * eyeScale + nudgeX, dhY + y * eyeScale + nudgeY, 'eyes', frame)
        .setDisplaySize(eyeW, eyeH)
        .setOrigin(0.5),
    );
    container.add(eyes);

    // Dog bowl to the right of the doghouse, sitting on the ground.
    const bowlW = 90;
    const bowlH = Math.round(bowlW * (42 / 50));
    const bowl = this.add
      .image(dhX + dhW / 2 + 30, dhY + dhH / 2 - 30, 'dogbowl2')
      .setDisplaySize(bowlW, bowlH)
      .setOrigin(0.5);
    container.add(bowl);

    // Eye-twitch timer — every 1s each eye randomly swaps among the 3 looks.
    const EYE_FRAMES = ['eyes1@2x.png', 'eyes2@2x.png', 'eyes3@2x.png'];
    const timer = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        for (const eye of eyes) {
          const f = EYE_FRAMES[Math.floor(Math.random() * EYE_FRAMES.length)]!;
          eye.setTexture('eyes', f);
        }
      },
    });
    container.once(Phaser.GameObjects.Events.DESTROY, () => timer.remove(false));
  }

  private advance(): void {
    if (this.pageIndex >= PAGES.length - 1) return;
    const outgoing = this.pageContainer;
    const incoming = this.buildPage(this.pageIndex + 1, LOGICAL_WIDTH);

    if (outgoing) {
      this.tweens.add({
        targets: outgoing,
        x: -LOGICAL_WIDTH,
        duration: 350,
        ease: 'Cubic.Out',
        onComplete: () => outgoing.destroy(true),
      });
    }
    this.tweens.add({
      targets: incoming,
      x: 0,
      duration: 350,
      ease: 'Cubic.Out',
    });
    this.pageIndex += 1;
  }

  private dismiss(): void {
    this.onComplete?.();
    this.scene.stop();
  }
}
